import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { Logo } from "@freightsimple/ui-core/Display/Logo";
import { PasswordInput } from "@freightsimple/ui-core/Controls/PasswordInput";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";
import { useState } from "react";
import useQuery from "@/Hooks/useQuery";
import Animation from "@/Shared/Animations/Animation";
import { useAuthenticationResetPassword } from "../Hooks/useAuthenticationResetPassword";
import { useSignInDirectly } from "@/Shared/Authentication/Hooks/useAuthenticationSignIn";

const MINIMUM_PASSWORD_LENGTH = 8;

interface AuthenticationResetPasswordProps {
  onBackToSignIn: () => void;
}

export function AuthenticationResetPassword(
  props: AuthenticationResetPasswordProps,
) {
  const { onBackToSignIn } = props;
  const { resetPassword, resetErrorMessage } = useAuthenticationResetPassword();
  const { signIn, continueAfterReset } = useSignInDirectly();
  const [signInErrorMessage, setSignInErrorMessage] =
    useState<ErrorMessageType>();
  const [newPassword, setNewPassword] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [expiresAt, setExpiresAt] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);
  const [wasSuccess, setWasSuccess] = useState(false);
  const query = useQuery();

  // query-string parses each param as string | string[] | null; the reset link
  // always carries a single value, so narrow to a plain string.
  const emailParam = query["email"];
  const tokenParam = query["token"];
  const email = typeof emailParam === "string" ? emailParam : "";
  const token = typeof tokenParam === "string" ? tokenParam : "";
  const passwordTooShort = newPassword.length < MINIMUM_PASSWORD_LENGTH;

  async function onResetPassword() {
    if (loading || passwordTooShort) {
      return;
    }
    setLoading(true);
    const success = await resetPassword(email, token, newPassword);
    if (success) {
      try {
        const { accessToken: newAccessToken, expiresAt: newExpiresAt } =
          await signIn(email, newPassword);
        setAccessToken(newAccessToken);
        setExpiresAt(newExpiresAt);
        setWasSuccess(true);
      } catch (error) {
        // The Auth0 client rejects with a description string, not an Error.
        setSignInErrorMessage(
          typeof error === "string"
            ? error
            : "Something went wrong signing you in. Please try again.",
        );
      }
    }
    setLoading(false);
  }

  if (wasSuccess) {
    return (
      <Stack gap={6}>
        <Logo />
        <Stack gap={4}>
          <Heading level={1} size="4xl" weight="semibold">
            Password Successfully Reset!
          </Heading>
          <T size="sm" color="secondary">
            You can now continue to use FreightSimple
          </T>
        </Stack>
        <Stack align="center">
          <Animation
            pathToAnimation="/animations/success-blue.json"
            lottieFilesUrl="https://lottiefiles.com/4431-success"
            loop
            speed={0.5}
            width={300}
          />
        </Stack>
        <Button
          primary
          fullWidth
          size="large"
          onClick={() => continueAfterReset(accessToken, expiresAt)}
          data-testid="reset-password-continue-button"
        >
          Continue
        </Button>
      </Stack>
    );
  }

  return (
    <ControlPresentationProvider presentation="underlined">
      {/* Pressing Enter submits; preventDefault stops the browser's native GET
          navigation and drives the same path as the button. */}
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onResetPassword();
        }}
        noValidate
      >
        <Stack gap={6}>
          <Stack gap={10}>
            <Logo />
            <Stack gap={1}>
              <Heading level={1} size="4xl" weight="semibold">
                Choose a new password
              </Heading>
              <T size="sm" color="muted">
                Pick a password of at least {MINIMUM_PASSWORD_LENGTH}{" "}
                characters.
              </T>
            </Stack>
          </Stack>

          <Stack gap={5}>
            <FormItem label="New Password" fillWidth emphasizeLabel>
              <PasswordInput
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                name="new_password"
                width="full"
                placeholder="Enter a new password"
                data-testid="reset-password-input"
              />
            </FormItem>
          </Stack>

          <Stack gap={4}>
            <Button
              primary
              fullWidth
              size="large"
              onClick={onResetPassword}
              loading={loading}
              disabled={loading || passwordTooShort}
              disabledMessage={
                passwordTooShort
                  ? `Use at least ${MINIMUM_PASSWORD_LENGTH} characters.`
                  : "Resetting your password…"
              }
              data-testid="reset-password-button"
            >
              Reset password
            </Button>

            {resetErrorMessage && (
              <T color="red" align="center">
                {resetErrorMessage}
              </T>
            )}
            {signInErrorMessage && (
              <T color="red" align="center">
                {signInErrorMessage}
              </T>
            )}

            <Stack align="center">
              <T color="secondary">
                <Link onClick={onBackToSignIn}>Back to sign in</Link>
              </T>
            </Stack>
          </Stack>
        </Stack>
      </form>
    </ControlPresentationProvider>
  );
}
